import styled, { keyframes } from 'styled-components';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';

import christmas_limited from './assets/christmas/limited.webp';
import christmas_rare from './assets/christmas/rare.webp';
import christmas_super_rare from './assets/christmas/super_rare.webp';
import christmas_unique from './assets/christmas/unique.webp';
import common from './assets/common.webp';
import custom_series from './assets/custom_series.webp';
import limited from './assets/limited.webp';
import playoffs_limited from './assets/playoffs-24/limited.webp';
import playoffs_rare from './assets/playoffs-24/rare.webp';
import playoffs_super_rare from './assets/playoffs-24/super_rare.webp';
import playoffs_unique from './assets/playoffs-24/unique.webp';
import rare from './assets/rare.webp';
import super_rare from './assets/super_rare.webp';
import unique from './assets/unique.webp';
import winterBackgroundUrl from './assets/winter/background.webp';
import winterLeftUrl from './assets/winter/freeze-left.webp';
import winterRightUrl from './assets/winter/freeze-right.webp';

const movingBackground = keyframes`
  from {
    background-position: 0 0;
  }
  to {
    background-position: 100% 0;
  }
`;

const BackgroundContainer = styled(Horizontal)`
  position: absolute;
  inset: 0;
  display: flex;
  animation: ${movingBackground} var(--duration) linear infinite;
`;

type Assets = Record<Rarity, string>;

const christmasAssets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${christmas_limited})`,
  [Rarity.rare]: `url(${christmas_rare})`,
  [Rarity.super_rare]: `url(${christmas_super_rare})`,
  [Rarity.unique]: `url(${christmas_unique})`,
};

const classic_assets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${limited})`,
  [Rarity.rare]: `url(${rare})`,
  [Rarity.super_rare]: `url(${super_rare})`,
  [Rarity.unique]: `url(${unique})`,
};

const playoffs_24_assets: Assets = {
  [Rarity.common]: `url(${common})`,
  [Rarity.custom_series]: `url(${custom_series})`,
  [Rarity.limited]: `url(${playoffs_limited})`,
  [Rarity.rare]: `url(${playoffs_rare})`,
  [Rarity.super_rare]: `url(${playoffs_super_rare})`,
  [Rarity.unique]: `url(${playoffs_unique})`,
};

export const WINTER_STYLE = `
  url("${winterLeftUrl}") left center / contain no-repeat,
  url("${winterRightUrl}") right center / contain no-repeat,
  url("${winterBackgroundUrl}") center / cover no-repeat
`;

export const WINTER_GOLDEN_STYLE = `
  linear-gradient( #FFC534,  #FFC534),
  url("${winterLeftUrl}") left center / contain no-repeat,
  url("${winterRightUrl}") right center / contain no-repeat,
  url("${winterBackgroundUrl}") center / cover no-repeat
`;
const winter: Assets = {
  [Rarity.common]: WINTER_STYLE,
  [Rarity.custom_series]: WINTER_STYLE,
  [Rarity.limited]: WINTER_STYLE,
  [Rarity.rare]: WINTER_STYLE,
  [Rarity.super_rare]: WINTER_STYLE,
  [Rarity.unique]: WINTER_STYLE,
};

type Background = {
  assets: Assets;
  duration: string;
  style?: React.CSSProperties;
};

const PLAYOFFS_24_VALUES = ['Postseason™', 'ace'];

const getBackgroundFromEditionName = (
  editionName?: string | null
): Background => {
  if (editionName) {
    if (editionName?.startsWith('neon_christmas')) {
      return {
        assets: christmasAssets,
        duration: '50s',
        style: {
          width: '900px',
        },
      };
    }
    if (PLAYOFFS_24_VALUES.includes(editionName)) {
      return {
        assets: playoffs_24_assets,
        duration: '25s',
      };
    }
    if (['winter', 'ice_breaker'].includes(editionName)) {
      return {
        assets: winter,
        duration: '25s',
        style: {
          animation: 'none',
          backgroundBlendMode: 'color-dodge, color-dodge, normal',
        },
      };
    }
  }
  return { assets: classic_assets, duration: '25s' };
};

type Props = {
  rarity: Rarity;
  editionName?: string | null;
};

export const ScarcityBackground = ({ rarity, editionName }: Props) => {
  const { assets, duration, style } = getBackgroundFromEditionName(editionName);

  return (
    <BackgroundContainer
      style={
        {
          background: assets[rarity],
          '--duration': duration,
          ...style,
        } as React.CSSProperties
      }
    />
  );
};
